import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

import { Banner as BannerImage } from 'assets/icons';

const DEFAULT_BANNER_CLOSED_KEY = 'banner_closed';
const BANNER_SHOW_DELAY_MS = 1500;

const isExternalUrl = (url: string): boolean => /^https?:\/\//i.test(url.trim());

interface BannerProps {
  imageSrc?: string;
  storageKey?: string;
  alt?: string;
  ctaText?: string;
  ctaUrl?: string;
}

const Banner: React.FC<BannerProps> = ({
  imageSrc = BannerImage,
  storageKey = DEFAULT_BANNER_CLOSED_KEY,
  alt = 'Promoção Clube da 42',
  ctaText,
  ctaUrl,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const hasCta = Boolean(ctaText?.trim() && ctaUrl?.trim());

  const closeBanner = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(storageKey, 'true');
  }, [storageKey]);

  useEffect(() => {
    const bannerClosed = localStorage.getItem(storageKey);
    if (bannerClosed) return;

    let cancelled = false;
    let scheduled = false;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const delay = prefersReducedMotion ? 0 : BANNER_SHOW_DELAY_MS;

    const scheduleShow = () => {
      if (cancelled || scheduled) return;
      scheduled = true;
      timerId = setTimeout(() => {
        if (!cancelled) setIsOpen(true);
      }, delay);
    };

    const img = new Image();
    img.onload = scheduleShow;
    img.onerror = scheduleShow;
    img.src = imageSrc;

    if (img.complete) {
      scheduleShow();
    }

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [imageSrc, storageKey]);

  const handleCtaClick = useCallback(() => {
    if (!ctaUrl?.trim()) return;

    const url = ctaUrl.trim();
    closeBanner();

    if (isExternalUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const path = url.startsWith('/') ? url : `/${url}`;
    navigate(path);
  }, [closeBanner, ctaUrl, navigate]);

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        p: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '95%', sm: '90%', md: 420, lg: 460 },
          maxWidth: '100%',
          maxHeight: { xs: '90vh', md: '85vh' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={imageSrc}
            alt={alt}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: hasCta
                ? { xs: 'calc(90vh - 72px)', md: 'calc(85vh - 72px)' }
                : { xs: '90vh', md: '85vh' },
              display: 'block',
              objectFit: 'contain',
              borderRadius: hasCta ? '8px 8px 0 0' : '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
          />
          <IconButton
            onClick={closeBanner}
            aria-label="Fechar banner"
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 12 },
              right: { xs: 8, sm: 12 },
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              },
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>

        {hasCta && (
          <Button
            onClick={handleCtaClick}
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: '0 0 8px 8px',
              bgcolor: '#f96100',
              color: '#fff',
              fontWeight: 700,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textTransform: 'none',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                bgcolor: '#e67a00',
              },
            }}
          >
            {ctaText?.trim()}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Banner;
